<template>
  <section class="section">
    <h2>Core Features That Save Organizers Time</h2>
    <p class="lead">Built for practical work on match day and before kickoff.</p>

    <div class="feature-grid">
      <article v-for="feature in features" :key="feature.title" class="feature-card">
        <h3>{{ feature.title }}</h3>
        <p>{{ feature.description }}</p>
      </article>
    </div>

    <div class="table-previews">
      <img :src="groupTableImage" alt="Live group standings table preview" loading="lazy" />
      <img :src="knockoutTableImage" alt="Knockout bracket preview" loading="lazy" />
    </div>
  </section>
</template>

<script setup>
import groupTableImage from "@/assets/images/tournament/grouptable.png";
import knockoutTableImage from "@/assets/images/tournament/knockouttable.png";

const features = [
  { title: "Fixture auto-generation", description: "Set up round robin or knockout format in seconds." },
  { title: "Live standings table", description: "Track every team position as results come in." },
  { title: "Player and team registration", description: "Collect complete squads without paperwork." },
  { title: "Automatic leaderboard updates", description: "Scores update rankings instantly after each match." },
  { title: "Admin dashboard", description: "Manage teams, fixtures, and reports from one place." },
];
</script>

<style scoped>
.section {
  padding: 1.9rem 0 0.9rem;
}

h2 {
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  color: var(--text-color1);
  font-weight: 800;
}

.lead {
  color: var(--text-color1);
  margin: 0.75rem 0 1.25rem;
  opacity: 0.88;
}

.feature-grid {
  display: grid;
  gap: 0.8rem;
}

.feature-card {
  border: 1px solid var(--border-color-hover1, #e5e7eb);
  border-radius: 18px;
  padding: 1rem;
  background: var(--background-color2);
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.06);
  transition: 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0px 8px 25px rgba(0, 0, 0, 0.12);
}

.feature-card h3 {
  margin: 0 0 0.45rem;
  font-size: 1.05rem;
}

.feature-card p {
  margin: 0;
  color: var(--text-color1);
  opacity: 0.85;
}

.table-previews {
  margin-top: 1.2rem;
  display: grid;
  gap: 0.75rem;
}

.table-previews img {
  width: 100%;
  border-radius: 16px;
  object-fit: cover;
  border: 1px solid var(--border-color-hover1, #e5e7eb);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.05);
}

@media (min-width: 768px) {
  .feature-grid {
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  }

  .table-previews {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
